import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as React from 'react';
import {
  Container, Typography, Stack, Box, Card,
  Radio, RadioGroup, FormControlLabel, FormControl,
  TextField, Autocomplete,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { validateUser, getLightPropostas, getUsuarios } from './api';
import config from '../config.json';
import { cores } from '../styles/styleUtils';
import Iconify from '../components/iconify';

// ── Cores das etapas ───────────────────────────────────────────────────
const CORES_ETAPAS = [
  '#1565C0', '#1976D2', '#1E88E5', '#42A5F5',
  '#64B5F6', '#90CAF9', '#BBDEFB', '#E3F2FD',
  '#0D47A1', '#1A237E', '#283593',
];

const ETAPAS_MONTAGEM = [
  'Recebimento da Montagem',
  'Agendamento com Cliente',
  'Retirada do Projeto',
  'Montagem',
  'Comissionamento',
  'Formulário de Montagem',
  'Solicitação de Vistoria',
  'Energização do Sistema',
  'Criação do Aplicativo',
  'Tutorial Cliente',
  'Finalização',
];

// Projetos computados a partir desta etapa (Formulário de Montagem = id 5)
const ETAPA_FORMULARIO_MONTAGEM = 5;

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// ── Card de KPI ────────────────────────────────────────────────────────
function KpiCard({ titulo, valor, icon, sub }) {
  return (
    <Card sx={{
      p: 2, minWidth: 150, flex: 1, textAlign: 'center',
      background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
      borderTop: `4px solid ${cores.laranja}`,
      borderRadius: 2, boxShadow: 3,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
    }}>
      {icon && <Iconify icon={icon} sx={{ color: cores.laranja, width: 22, height: 22 }} />}
      <Typography sx={{ color: '#bdbdbd', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {titulo}
      </Typography>
      <Typography sx={{ color: cores.textoClaro, fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
        {valor}
      </Typography>
      {sub && <Typography sx={{ color: '#9e9e9e', fontSize: 11 }}>{sub}</Typography>}
    </Card>
  );
}

// ── Filtro de período ─────────────────────────────────────────────────
function FiltroPeriodo({ filtro, setFiltro, usuarios, isSupervisor }) {
  return (
    <Stack direction="row" gap={2} flexWrap="wrap" mb={2}>
      <TextField
        size="small" type="date" label="Data Inicial"
        value={filtro.dataInicial} InputLabelProps={{ shrink: true }}
        onChange={(e) => setFiltro((f) => ({ ...f, dataInicial: e.target.value }))}
        sx={{ backgroundColor: 'white', minWidth: 160 }}
      />
      <TextField
        size="small" type="date" label="Data Final"
        value={filtro.dataFinal} InputLabelProps={{ shrink: true }}
        onChange={(e) => setFiltro((f) => ({ ...f, dataFinal: e.target.value }))}
        sx={{ backgroundColor: 'white', minWidth: 160 }}
      />
      {isSupervisor && (
        <Autocomplete
          size="small" options={usuarios} sx={{ minWidth: 200, backgroundColor: 'white' }}
          getOptionLabel={(o) => o.nome} value={filtro.montador || null}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          onChange={(_, v) => setFiltro((f) => ({ ...f, montador: v }))}
          renderInput={(p) => <TextField {...p} label="Montador" />}
        />
      )}
    </Stack>
  );
}

// ── Componente principal ───────────────────────────────────────────────
export default function HomeMontagem() {
  const navigate = useNavigate();
  const usuarioLogado = JSON.parse(localStorage.getItem('userData'));
  const isSupervisor = usuarioLogado?.supervisor;
  const isMontador   = usuarioLogado?.montador;
  const isEngenheiro = !isSupervisor && !isMontador && usuarioLogado?.modulos?.some(m => m.id === 2 && m.ativo);

  const [propostas, setPropostas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [radioFunil, setRadioFunil] = useState('quantidade');
  const [radioMontagens, setRadioMontagens] = useState('quantidade');

  // Período padrão: mês corrente
  const dataAtual = new Date();
  const primeiroDia = new Date(dataAtual.getFullYear(), 0, 1)
    .toISOString().split('T')[0];
  const ultimoDia = new Date(dataAtual.getFullYear(), dataAtual.getMonth() + 1, 0)
    .toISOString().split('T')[0];

  const [filtro, setFiltro] = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, montador: null });
  const [filtroFunil,     setFiltroFunil]     = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, montador: null });
  const [filtroEstados,   setFiltroEstados]   = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, montador: null });
  const [filtroSemanas,   setFiltroSemanas]   = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, montador: null });
  const [filtroReceita,   setFiltroReceita]   = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, montador: null });
  const [filtroMontagens, setFiltroMontagens] = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, montador: null });

  // Valida sessão
  useEffect(() => {
    const validateuser = async () => {
      const teste = await validateUser();
      if (!teste.ok) {
        localStorage.removeItem('userData');
        localStorage.removeItem('userToken');
        config.user = {};
        navigate('/', { replace: true });
        return;
      }
      localStorage.setItem('userData', JSON.stringify(teste.usuario));
      config.user = teste.usuario;
    };
    validateuser();
  }, []);

  // Carrega usuários para filtro do supervisor
  useEffect(() => {
    const fetchAux = async () => {
      const u = await getUsuarios();
      setUsuarios(u || []);
    };
    fetchAux();
  }, []);

    // Carrega propostas do fluxo de montagem (fluxo.id === 4)
  useEffect(() => {
    const fetchPropostas = async () => {
      const filtroApi = {
        montagem: true,
        datainicial: filtro.dataInicial,
        datafinal: filtro.dataFinal,
        usuarioLogado,
        // Engenheiro vê projetos onde é responsável; montador vê onde é montador
        ...(isEngenheiro && { responsavel: usuarioLogado }),
        ...(isMontador   && { montador: usuarioLogado }),
        // Supervisor pode filtrar por montador via UI
        ...(isSupervisor && filtro.montador && { montador: filtro.montador }),
      };
      const result = await getLightPropostas(filtroApi);
      setPropostas(result || []);
    };
    fetchPropostas();
  }, [filtro]);

  // Filtro local por gráfico — aplica também restrição de perfil
  const filtrarLocal = useCallback((lista, f) => lista.filter((p) => {
    const d = new Date(p.data);
    const di = f.dataInicial ? new Date(f.dataInicial) : null;
    const df = f.dataFinal ? new Date(`${f.dataFinal}T23:59:59`) : null;
    if (di && d < di) return false;
    if (df && d > df) return false;
    // Supervisor: filtra por montador selecionado na UI (opcional)
    if (isSupervisor && f.montador && p.montador?._id?.toString() !== f.montador._id?.toString()) return false;
    // Engenheiro: apenas projetos onde é responsável (usa toString para evitar problema ObjectId vs string)
    if (isEngenheiro && p.responsavel?._id?.toString() !== usuarioLogado._id?.toString()) return false;
    // Montador: apenas projetos onde é montador
    if (isMontador && p.montador?._id?.toString() !== usuarioLogado._id?.toString()) return false;
    return true;
  }), [isSupervisor, isEngenheiro, isMontador, usuarioLogado._id]);

  const propostasFunil     = useMemo(() => filtrarLocal(propostas, filtroFunil),     [propostas, filtroFunil, isSupervisor, isEngenheiro, isMontador]);
  const propostasEstados   = useMemo(() => filtrarLocal(propostas, filtroEstados),   [propostas, filtroEstados, isSupervisor, isEngenheiro, isMontador]);
  const propostasSemanas   = useMemo(() => filtrarLocal(propostas, filtroSemanas),   [propostas, filtroSemanas, isSupervisor, isEngenheiro, isMontador]);
  const propostasReceita   = useMemo(() => filtrarLocal(propostas, filtroReceita),   [propostas, filtroReceita, isSupervisor, isEngenheiro, isMontador]);
  const propostasMontagens = useMemo(() => filtrarLocal(propostas, filtroMontagens), [propostas, filtroMontagens, isSupervisor, isEngenheiro, isMontador]);

  // ── KPIs ─────────────────────────────────────────────────────────────
  const projetosFormulario = propostas.filter((p) => p.etapa?.id >= ETAPA_FORMULARIO_MONTAGEM);
  const totalProjetos  = propostas.length;
  const totalKwp       = propostas.reduce((s, p) => s + (p.kit?.potenciacc || 0), 0);
  const totalReceita   = projetosFormulario.reduce((s, p) => s + (p.valormontagem || 0), 0);
  const projetosAtivos = propostas.filter((p) => p.etapa?.id >= 1 && p.etapa?.id < 10).length;
  const ticketMedio    = projetosFormulario.length > 0
    ? (projetosFormulario.reduce((s, p) => s + (p.kit?.potenciacc || 0), 0) / projetosFormulario.length).toFixed(2)
    : 0;

  // ── Funil por etapa ───────────────────────────────────────────────────
  const dadosFunil = useMemo(() => ETAPAS_MONTAGEM.map((etapa, i) => {
    const propsEtapa = propostasFunil.filter((p) => p.etapa?.nome === etapa);
    return {
      name:       etapa,
      quantidade: propsEtapa.length,
      kwp:        parseFloat(propsEtapa.reduce((s, p) => s + (p.kit?.potenciacc || 0), 0).toFixed(2)),
      // off-grid usa kit.modulo.qtde; on-grid usa kit.quantidademodulos
      placas:     propsEtapa.reduce((s, p) => s + (p.kit?.quantidademodulos || p.kit?.modulo?.qtde || 0), 0),
      fill:       CORES_ETAPAS[i] || '#1565C0',
    };
  }), [propostasFunil]);

  // ── Projetos por estado ───────────────────────────────────────────────
  const dadosEstados = useMemo(() => {
    const mapa = {};
    propostasEstados.forEach((p) => {
      const estado = p.localinstalacao?.estado?.nome || p.localinstalacao?.estado || 'Não informado';
      mapa[estado] = (mapa[estado] || 0) + 1;
    });
    return Object.entries(mapa)
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 10);
  }, [propostasEstados]);

  // ── Projetos por semana ───────────────────────────────────────────────
  const dadosSemanas = useMemo(() => {
    const semanas  = ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'];
    const contagem = [0, 0, 0, 0];
    const hoje = new Date();
    propostasSemanas.forEach((p) => {
      const d = new Date(p.data);
      if (d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear()) {
        const dia = d.getDate();
        if      (dia <= 7)  contagem[0] += 1;
        else if (dia <= 14) contagem[1] += 1;
        else if (dia <= 21) contagem[2] += 1;
        else                contagem[3] += 1;
      }
    });
    return semanas.map((s, i) => ({ semana: s, quantidade: contagem[i] }));
  }, [propostasSemanas]);

  // ── Receita de montagem por mês ───────────────────────────────────────
  const dadosReceita = useMemo(() => {
    const meses    = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const receitas = Array(12).fill(0);
    propostasReceita
      .filter((p) => p.etapa?.id >= ETAPA_FORMULARIO_MONTAGEM)
      .forEach((p) => {
        if (p.valormontagem) {
          receitas[new Date(p.data).getMonth()] += p.valormontagem;
        }
      });
    return meses.map((mes, i) => ({ mes, receita: parseFloat(receitas[i].toFixed(2)) }));
  }, [propostasReceita]);

  // ── Montagens por mês ─────────────────────────────────────────────────
  const dadosMontagens = useMemo(() => {
    const meses  = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const valores = Array(12).fill(0);
    propostasMontagens
      .filter((p) => p.etapa?.id >= ETAPA_FORMULARIO_MONTAGEM)
      .forEach((p) => {
        const mes = new Date(p.data).getMonth();
        if      (radioMontagens === 'quantidade') valores[mes] += 1;
        else if (radioMontagens === 'placas')     valores[mes] += (p.kit?.quantidademodulos || p.kit?.modulo?.qtde || 0);
        else if (radioMontagens === 'kwp')        valores[mes] += (p.kit?.potenciacc || 0);
      });
    return meses.map((mes, i) => ({ mes, valor: parseFloat(valores[i].toFixed(2)) }));
  }, [propostasMontagens, radioMontagens]);

  const labelMontagens = radioMontagens === 'quantidade' ? 'Projetos' : radioMontagens === 'placas' ? 'Placas' : 'kWp';

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet><title>Home — Montagem</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="#1565C0" mb={3}>
          Dashboard Montagem
        </Typography>

        {/* ── Cards KPI ── */}
        <Stack direction="row" gap={2} flexWrap="wrap" mb={4}>
          <KpiCard titulo="Total de Projetos"    valor={fmtNum(totalProjetos)} icon="eva:clipboard-outline" />
          <KpiCard titulo="Potência Total"        valor={`${totalKwp.toFixed(2)} kWp`} icon="game-icons:solar-power" />
          <KpiCard titulo="Em Andamento"          valor={fmtNum(projetosAtivos)}         icon="eva:activity-outline" />
          <KpiCard titulo="Ticket Médio (kWp)"   valor={`${ticketMedio} kWp`}           icon="eva:flash-outline" />
          <KpiCard titulo="Receita Montagens"     valor={fmtBRL(totalReceita)}           icon="eva:trending-up-outline" />
        </Stack>

        {/* ── Funil de Etapas ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight="bold" color="#1565C0">
              Funil de Projetos por Etapa
            </Typography>
            <FormControl>
              <RadioGroup row value={radioFunil} onChange={(e) => setRadioFunil(e.target.value)}>
                <FormControlLabel value="quantidade" control={<Radio size="small" />} label="Quantidade" />
                <FormControlLabel value="kwp"        control={<Radio size="small" />} label="kWp" />
                <FormControlLabel value="placas"     control={<Radio size="small" />} label="Qtd. Placas" />
              </RadioGroup>
            </FormControl>
          </Stack>
        <FiltroPeriodo filtro={filtroFunil} setFiltro={setFiltroFunil} usuarios={usuarios} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={dadosFunil} layout="vertical" margin={{ left: 180 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={175} />
              <Tooltip />
              <Bar dataKey={radioFunil} radius={[0, 4, 4, 0]}>
                {dadosFunil.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Projetos por Estado ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={1}>
            Projetos por Estado
          </Typography>
          <FiltroPeriodo filtro={filtroEstados} setFiltro={setFiltroEstados} usuarios={usuarios} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dadosEstados} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="nome" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="quantidade" fill="#1565C0" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Projetos por Semana ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#e65100" mb={1}>
            Projetos Encaminhados — Semanas do Mês Atual
          </Typography>
          <FiltroPeriodo filtro={filtroSemanas} setFiltro={setFiltroSemanas} usuarios={usuarios} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dadosSemanas} margin={{ top: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="semana" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="quantidade" fill="#e65100" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Receita de Montagem por Mês ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#6a1b9a" mb={1}>
            Receita de Montagem por Mês
            <Typography component="span" variant="caption" color="text.secondary" ml={1}>
              (a partir do Formulário de Montagem)
            </Typography>
          </Typography>
          <FiltroPeriodo filtro={filtroReceita} setFiltro={setFiltroReceita} usuarios={usuarios} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dadosReceita} margin={{ top: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtBRL(v)} />
              <Bar dataKey="receita" fill="#6a1b9a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Montagens por Mês ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight="bold" color="#2e7d32">
              Montagens por Mês
              <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                (a partir do Formulário de Montagem)
              </Typography>
            </Typography>
            <FormControl>
              <RadioGroup row value={radioMontagens} onChange={(e) => setRadioMontagens(e.target.value)}>
                <FormControlLabel value="quantidade" control={<Radio size="small" />} label="Qtd. Projetos" />
                <FormControlLabel value="placas"     control={<Radio size="small" />} label="Qtd. Placas" />
                <FormControlLabel value="kwp"        control={<Radio size="small" />} label="kWp" />
              </RadioGroup>
            </FormControl>
          </Stack>
          <FiltroPeriodo filtro={filtroMontagens} setFiltro={setFiltroMontagens} usuarios={usuarios} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dadosMontagens} margin={{ top: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis
                allowDecimals={radioMontagens === 'kwp'}
                tickFormatter={radioMontagens === 'kwp' ? (v) => `${v} kWp` : undefined}
              />
              <Tooltip formatter={(v) => radioMontagens === 'kwp' ? `${v} kWp` : fmtNum(v)} />
              <Bar dataKey="valor" name={labelMontagens} fill="#2e7d32" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

      </Container>
    </>
  );
}